import { useEffect, useState, type FormEvent } from 'react';
import { upsertDayAnnotationWithTags } from '@/lib/scheduling';
import type { DayAnnotation } from '@/lib/types';

export interface DayAnnotationModalProps {
  open: boolean;
  date: string;
  locationId: string;
  availableTags: string[];
  current: DayAnnotation | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

export function DayAnnotationModal(props: DayAnnotationModalProps) {
  const { open, date, locationId, availableTags, current, onClose, onSaved } = props;

  const [tags, setTags] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTags(current?.tags ?? []);
    setNote(current?.note ?? '');
    setError(null);
  }, [open, current]);

  if (!open) return null;

  function toggleTag(t: string) {
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await upsertDayAnnotationWithTags(locationId, date, note.trim() || null, tags);
      await onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete() {
    if (!confirm('Remove this day note and all tags?')) return;
    setSubmitting(true);
    setError(null);
    try {
      await upsertDayAnnotationWithTags(locationId, date, null, []);
      await onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg border border-border bg-card shadow-lg">
        <header className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-base font-medium">Day note · {date}</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">×</button>
        </header>

        <form onSubmit={onSubmit} className="p-4 space-y-4">
          <div className="space-y-2">
            <p className="block text-xs font-medium">Tags</p>
            <p className="text-[11px] text-muted-foreground">Pick any that apply. Tagged days are highlighted in amber.</p>
            <div className="flex flex-wrap gap-1.5">
              {availableTags.length === 0 ? (
                <p className="text-xs text-muted-foreground">No tags configured for this pharmacy yet. Manage in Locations → Tags.</p>
              ) : (
                availableTags.map((t) => {
                  const active = tags.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTag(t)}
                      className={
                        'text-xs px-2.5 py-1 rounded-full border ' +
                        (active
                          ? 'bg-amber-100 border-amber-600 text-amber-900 font-medium'
                          : 'bg-card border-border text-muted-foreground hover:bg-muted/40')
                      }
                    >
                      {active ? '✓ ' : ''}{t}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="space-y-1">
            <label htmlFor="day_note" className="block text-xs font-medium">Free-text day note</label>
            <textarea
              id="day_note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Stock arrives 14:00. Walk-ins busy."
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>

          {error && <p className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">{error}</p>}

          <footer className="flex items-center justify-between pt-2 border-t border-border">
            <div>
              {current && (
                <button type="button" onClick={onDelete} disabled={submitting} className="text-xs text-destructive hover:underline">Remove note</button>
              )}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40">Close</button>
              <button type="submit" disabled={submitting} className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-sm font-medium hover:opacity-90 disabled:opacity-50">{submitting ? 'Saving…' : 'Save'}</button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}
